import { never } from "./errors";
import { createRoot, Root } from "react-dom/client";
import { BusyIndicator, Portal } from "client/Components";

type MountedIndicator = {
	stackCount: number;
	root: Root;
};

const mountedIndicatorsByElement = new WeakMap<HTMLElement, MountedIndicator>();

export type ContainerType = "#main-content" | "body" | undefined;

// When the container parameter is undefined it takes '#main-content' as container
export function show(container?: ContainerType): void {
	const element: HTMLElement | null = resolveElement(container);

	if (!element) {
		return;
	}

	const indicator: MountedIndicator | undefined = mountedIndicatorsByElement.get(element);

	if (indicator) {
		indicator.stackCount++;
		return;
	}

	const host: HTMLElement = document.createElement("div");
	const root: Root = createRoot(host);

	mountedIndicatorsByElement.set(element, { stackCount: 1, root });

	root.render(
		<Portal container={element}>
			<BusyIndicator />
		</Portal>
	);
}

export function hide(container?: ContainerType): void {
	const element: HTMLElement | null = resolveElement(container);

	if (!element) {
		return;
	}

	const indicator: MountedIndicator | undefined = mountedIndicatorsByElement.get(element);

	if (!indicator) {
		return;
	}

	indicator.stackCount--;

	if (indicator.stackCount <= 0) {
		mountedIndicatorsByElement.delete(element);
		indicator.root
			.unmount();
	}
}

function resolveElement(container: ContainerType): HTMLElement | null {
	switch (container) {
		case "body":
			return document.body;
		case undefined:
		case "#main-content":
			return document.getElementById("main-content");
		default:
			never(container);
	}
}
